import { ImagePlus, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import api from "../../../configs/api";
import uploadFileToCloudinary from "../../../utils/uploadFileToCloudinary";
import { ModalWrapper } from "../common/ModalWrapper";
import toast from "react-hot-toast";

const EditProductModal = ({ modal, onProductUpdated }) => {
  const { isOpen, mode, data: productId, close } = modal;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: "",
    tagline: "",
    description: "",
    closingText: "",
    features: [""],
    images: [],
    coverImage: "",
  });

  const [uploads, setUploads] = useState([]);
  const [coverUpload, setCoverUpload] = useState(null);

  // useEffect(() => {
  //   console.log("uploads:", uploads);
  // }, [uploads]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchProduct = async () => {
      if (mode !== "edit" || !productId) return;

      try {
        setLoading(true);

        const res = await api.get(`/products/${productId}`);
        const product = res.data?.data;

        setForm({
          name: product.name || "",
          tagline: product.tagline || "",
          description: product.description || "",
          closingText: product.closingText || "",
          features: product.features?.length ? product.features : [""],
          images: product.images || [],
          coverImage: product.coverImage || "",
        });

        const uploads = product?.images?.map((img) => ({
          blobUrl: null,
          progress: 0,
          uploaded: true,
          url: img,
        }));

        setUploads(uploads);
        if (product.coverImage) {
          setCoverUpload({
            blobUrl: null,
            progress: 0,
            uploaded: true,
            url: product.coverImage,
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (mode === "create") {
      setForm({
        name: "",
        tagline: "",
        description: "",
        closingText: "",
        features: [""],
        images: [],
        coverImage: "",
      });
    }

    fetchProduct();
  }, [isOpen, mode, productId]);

  /*  FIELD UPDATE  */
  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  /*  FEATURES  */
  const updateFeature = (index, value) => {
    const updated = [...form.features];
    updated[index] = value;

    setForm((prev) => ({
      ...prev,
      features: updated,
    }));
  };

  const addFeature = () => {
    setForm((prev) => ({
      ...prev,
      features: [...prev.features, ""],
    }));
  };

  const removeFeature = (index) => {
    const updated = form.features.filter((_, i) => i !== index);

    setForm((prev) => ({
      ...prev,
      features: updated.length ? updated : [""],
    }));
  };

  /*  IMAGE PICKER  */
  const handleImageSelect = (files) => {
    const fileList = Array.from(files);

    fileList.forEach((file) => {
      const blobUrl = URL.createObjectURL(file);

      const uploadItem = {
        blobUrl,
        progress: 0,
        uploaded: false,
        url: null,
      };

      setUploads((prev) => [...prev, uploadItem]);

      const { uploadPromise } = uploadFileToCloudinary(file, (percent) => {
        setUploads((prev) =>
          prev.map((u) =>
            u.blobUrl === blobUrl ? { ...u, progress: percent } : u,
          ),
        );
      });

      uploadPromise.then((res) => {
        if (!res) return;

        setUploads((prev) =>
          prev.map((u) =>
            u.blobUrl === blobUrl
              ? { ...u, uploaded: true, url: res.url, progress: 100 }
              : u,
          ),
        );

        setForm((prev) => ({
          ...prev,
          images: [...prev.images, res.url],
        }));
      });
    });
  };

  const removeImage = (index) => {
    const updated = form.images.filter((_, i) => i !== index);

    setForm((prev) => ({
      ...prev,
      images: updated,
    }));

    setUploads((prev) => prev.filter((_, i) => i !== index));
  };

  /* cover image upload and remove */
  const handleCoverImageSelect = (file) => {
    const blobUrl = URL.createObjectURL(file);

    const uploadItem = {
      blobUrl,
      progress: 0,
      uploaded: false,
      url: null,
    };

    setCoverUpload(uploadItem);

    const { uploadPromise } = uploadFileToCloudinary(file, (percent) => {
      setCoverUpload((prev) => ({ ...prev, progress: percent }));
    });

    uploadPromise.then((res) => {
      if (!res) return;

      setCoverUpload({
        blobUrl: null,
        uploaded: true,
        url: res.url,
        progress: 100,
      });

      setForm((prev) => ({
        ...prev,
        coverImage: res.url,
      }));
    });
  };

  const removeCoverImage = () => {
    setCoverUpload(null);
    setForm((prev) => ({
      ...prev,
      coverImage: "",
    }));
  };

  /*  SUBMIT  */
  const handleSubmit = async () => {
    try {
      setSaving(true);
      let res;

      if (mode === "create") {
        res = await api.post("/products", form);
        toast.success("Product created successfully");
      } else {
        res = await api.patch(`/products/${productId}`, form);
        toast.success("product upated successfully");
      }
      const product = res.data?.data;

      if (mode === "edit" && onProductUpdated) {
        onProductUpdated(product);
      }
      close();
    } catch (err) {
      console.error(err);
      toast.error("Product action failed");
    } finally {
      setSaving(false);
    }
  };

  /*  IMAGE GRID  */

  const renderImages = () => {
    if (!uploads.length) return null;

    return (
      <div className="grid grid-cols-4 gap-3 mt-3">
        {uploads.map((img, index) => (
          <div
            key={index}
            className="relative aspect-square border border-borderLight overflow-hidden"
          >
            <img
              alt="img"
              src={img.uploaded ? img.url : img.blobUrl}
              className="w-full h-full object-cover"
            />

            {!img.uploaded && (
              <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white text-xs">
                <Loader2 className="animate-spin mb-1" size={16} />
                {img.progress}%
              </div>
            )}

            <button
              onClick={() => removeImage(index)}
              className="absolute top-1 right-1 bg-black/70 hover:text-red-500 hover:bg-red-200 transition-all duration-50 cursor-pointer text-white p-1"
            >
              <FiTrash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    );
  };

  /*  UI  */

  return (
    <ModalWrapper
      title={mode === "create" ? "Create Product" : "Edit Product"}
      isOpen={isOpen}
      onClose={close}
      primaryActionLabel={mode === "create" ? "Create Product" : "Save Changes"}
      primaryLoading={saving}
      allowEasyClose={false}
      onPrimaryAction={handleSubmit}
      maxWidth="max-w-3xl"
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin" />
        </div>
      ) : (
        <div className="space-y-5 text-sm">
          {/* NAME */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Product Name
            </label>

            <input
              value={form.name}
              placeholder="e.g. TEXTUREMIX"
              onChange={(e) => updateField("name", e.target.value)}
              className="mt-1 h-[44px] w-full text-sm px-4 border border-borderLight outline-none focus:border-primaryAccent"
            />
          </div>

          {/* TAGLINE */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Tagline
            </label>

            <input
              placeholder="e.g. Premium Concrete for Stamped Designs"
              value={form.tagline}
              onChange={(e) => updateField("tagline", e.target.value)}
              className="mt-1 h-[44px] w-full text-sm px-4 border border-borderLight outline-none focus:border-primaryAccent"
            />
          </div>

          {/* DESCRIPTION */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Description
            </label>

            <textarea
              rows={4}
              placeholder="e.g. High-performance concrete mix designed for decorative stamped surfaces and long-lasting durability."
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              className="mt-1 w-full px-4 py-3 border border-borderLight outline-none focus:border-primaryAccent"
            />
          </div>

          {/* FEATURES */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Features
            </label>

            <div className="space-y-2 mt-2">
              {form.features.map((f, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    placeholder="e.g. Durable & Long-Lasting – Resistant to wear, cracking, and fading."
                    value={f}
                    onChange={(e) => updateFeature(i, e.target.value)}
                    className="flex-1 h-[42px] px-4 border border-borderLight outline-none focus:border-primaryAccent"
                  />

                  <button
                    onClick={() => removeFeature(i)}
                    className="w-10 h-[42px] border border-borderLight flex items-center justify-center hover:text-red-500 hover:bg-red-100"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              ))}
            </div>

            <button
              onClick={addFeature}
              className="mt-3 flex items-center gap-2 text-sm text-primaryAccent"
            >
              <FiPlus />
              Add Feature
            </button>
          </div>

          {/* CLOSING TEXT */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Closing Text
            </label>

            <textarea
              placeholder="e.g. Ideal for decorative driveways, patios, walkways and stamped concrete surfaces."
              rows={3}
              value={form.closingText}
              onChange={(e) => updateField("closingText", e.target.value)}
              className="mt-1 w-full px-4 py-3 border border-borderLight outline-none focus:border-primaryAccent"
            />
          </div>

          {/* COVER IMAGE */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Cover Image
            </label>

            {!coverUpload && !form.coverImage && (
              <label
                className="
      mt-2 flex flex-col items-center justify-center
      border-2 border-dashed border-borderLight
      h-[140px] cursor-pointer hover:border-primaryAccent
      transition"
              >
                <ImagePlus size={28} className="text-descriptionText mb-2" />
                <span className="text-xs text-gray-400 text-center">
                  Upload cover image (Main product image)
                </span>

                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => handleCoverImageSelect(e.target.files[0])}
                />
              </label>
            )}

            {(coverUpload || form.coverImage) && (
              <div className="relative mt-3 w-40 h-28 border border-borderLight overflow-hidden">
                <img
                  src={
                    coverUpload
                      ? coverUpload.uploaded
                        ? coverUpload.url
                        : coverUpload.blobUrl
                      : form.coverImage
                  }
                  className="w-full h-full object-cover"
                  alt="cover"
                />

                {/* Loader */}
                {coverUpload && !coverUpload.uploaded && (
                  <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white text-xs">
                    <Loader2 className="animate-spin mb-1" size={16} />
                    {coverUpload.progress}%
                  </div>
                )}

                {/* Remove */}
                <button
                  onClick={removeCoverImage}
                  className="absolute top-1 right-1 bg-black/70 text-white p-1 hover:text-red-500"
                >
                  <FiTrash2 size={14} />
                </button>
              </div>
            )}
          </div>

          {/* IMAGE UPLOADER */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Product Images
            </label>

            <label
              className="
              mt-2 flex flex-col items-center justify-center
              border-2 border-dashed border-borderLight
              h-[140px] cursor-pointer hover:border-primaryAccent
              transition"
            >
              <ImagePlus size={28} className="text-descriptionText mb-2" />
              <span className="text-xs text-gray-400 text-center ">
                Upload product images (JPG / PNG) <br /> Example: product
                texture, construction site, finished pavement{" "}
              </span>

              <input
                type="file"
                multiple
                hidden
                accept="image/*"
                onChange={(e) => handleImageSelect(e.target.files)}
              />
            </label>

            {renderImages()}
          </div>
        </div>
      )}
    </ModalWrapper>
  );
};

export default EditProductModal;
